import { useCart } from "@/contexts/CartContext";
import { X, Plus, Minus, ShoppingBag, Trash2, CreditCard, Tag, Check } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useState, useEffect, useRef } from "react";

// Loads the PayPal JS SDK once per page-load.
function loadPayPalSdk(clientId) {
  if (typeof window === "undefined") return Promise.reject("no window");
  if (window.paypal) return Promise.resolve(window.paypal);
  if (window.__paypalSdkPromise) return window.__paypalSdkPromise;
  window.__paypalSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture`;
    script.async = true;
    script.onload = () => resolve(window.paypal);
    script.onerror = () => reject(new Error("Failed to load PayPal SDK"));
    document.head.appendChild(script);
  });
  return window.__paypalSdkPromise;
}

export default function CartDrawer() {
  const { items, removeItem, updateQuantity, clearCart, total, isOpen, setIsOpen } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  const paypalContainerRef = useRef(null);
  const paypalButtonsInstanceRef = useRef(null);

  // Inner Circle welcome discount
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [validating, setValidating] = useState(false);
  const [savedCode, setSavedCode] = useState("");

  const discountAmount = discountPercent ? (total * discountPercent) / 100 : 0;
  const grandTotal = Math.max(0, total - discountAmount);

  // Suggest the customer's saved welcome code (from popup/footer signup)
  useEffect(() => {
    if (!isOpen) return;
    try {
      const saved = localStorage.getItem("fleuris_welcome_code");
      if (saved) { setSavedCode(saved); if (!appliedCode) setCodeInput((c) => c || saved); }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const applyCode = async (raw) => {
    const code = (raw || codeInput || "").trim();
    if (!code) return;
    setValidating(true);
    try {
      const res = await axios.post(`${API}/discount/validate`, { code });
      if (res.data?.valid) {
        setAppliedCode(code.toUpperCase());
        setDiscountPercent(res.data.percent || 0);
        toast.success(`${res.data.percent}% welcome discount applied`);
      } else {
        setAppliedCode(""); setDiscountPercent(0);
        toast.error("That code isn't valid or has already been used.");
      }
    } catch {
      toast.error("Couldn't check that code. Please try again.");
    } finally {
      setValidating(false);
    }
  };

  const removeCode = () => {
    setAppliedCode(""); setDiscountPercent(0); setCodeInput(savedCode || "");
  };

  // Lazy-load PayPal SDK when drawer opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/paypal/client-id`);
        if (cancelled) return;
        if (res.data?.configured && res.data?.client_id) {
          await loadPayPalSdk(res.data.client_id);
          if (!cancelled) setPaypalReady(true);
        }
      } catch (e) {
        console.warn("PayPal SDK init failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleStripeCheckout = async () => {
    if (items.length === 0) return;
    setCheckingOut(true);
    try {
      const firstItem = items[0];
      const payload = { product_id: firstItem.id, origin_url: window.location.origin };
      if (firstItem.variantIndex !== null) payload.variant_index = firstItem.variantIndex;
      if (firstItem.deliveryDate) payload.delivery_date = firstItem.deliveryDate;
      if (firstItem.deliveryTime) payload.delivery_time = firstItem.deliveryTime;
      if (appliedCode) payload.discount_code = appliedCode;
      const res = await axios.post(`${API}/checkout/stripe`, payload);
      window.location.href = res.data.url;
    } catch {
      toast.error("Checkout failed. Please try again.");
    } finally {
      setCheckingOut(false);
    }
  };

  // Render the PayPal Smart Button(s) whenever items change.
  useEffect(() => {
    if (!isOpen || !paypalReady || !paypalContainerRef.current || items.length === 0) return;
    if (!window.paypal) return;

    if (paypalButtonsInstanceRef.current) {
      try { paypalButtonsInstanceRef.current.close(); } catch { /* ignore */ }
      paypalButtonsInstanceRef.current = null;
    }
    paypalContainerRef.current.replaceChildren();

    const buttons = window.paypal.Buttons({
      style: { layout: "horizontal", color: "gold", shape: "rect", label: "paypal", tagline: false, height: 44 },
      createOrder: async () => {
        const firstItem = items[0];
        const payload = { product_id: firstItem.id, origin_url: window.location.origin };
        if (firstItem.variantIndex !== null) payload.variant_index = firstItem.variantIndex;
        if (firstItem.deliveryDate) payload.delivery_date = firstItem.deliveryDate;
        if (firstItem.deliveryTime) payload.delivery_time = firstItem.deliveryTime;
        if (appliedCode) payload.discount_code = appliedCode;
        const res = await axios.post(`${API}/checkout/paypal`, payload);
        return res.data.order_id;
      },
      onApprove: async (data) => {
        try {
          await axios.post(`${API}/checkout/paypal/${data.orderID}/capture`);
          window.location.href = `/checkout/success?paypal_order_id=${data.orderID}`;
        } catch {
          toast.error("Payment approved but capture failed. Please contact hello@fleurisnoir.com.");
        }
      },
      onError: () => toast.error("PayPal encountered an error. Please try again."),
      onCancel: () => toast.info("PayPal payment cancelled."),
    });

    buttons.render(paypalContainerRef.current).catch(err => console.error("PayPal render failed", err));
    paypalButtonsInstanceRef.current = buttons;

    return () => {
      if (paypalButtonsInstanceRef.current) {
        try { paypalButtonsInstanceRef.current.close(); } catch { /* ignore */ }
        paypalButtonsInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, paypalReady, items.length, items.map(i => i.key + i.quantity).join(","), appliedCode]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-[#0c0c0c]/70 backdrop-blur-sm z-[80]" onClick={() => setIsOpen(false)} />
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-[#161616] border-l border-[#b8966a]/20 z-[90] flex flex-col" data-testid="cart-drawer">
        <div className="flex items-center justify-between p-6 border-b border-[#b8966a]/10">
          <div className="flex items-center gap-3">
            <ShoppingBag size={20} className="text-[#b8966a]" />
            <h2 className="font-heading text-xl text-[#f8f5f0]">Your Cart</h2>
          </div>
          <button onClick={() => setIsOpen(false)} className="p-2 text-[#a19d98] hover:text-[#f8f5f0] transition-colors" data-testid="close-cart">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="text-[#b8966a]/30 mx-auto mb-4" size={40} />
              <p className="font-body text-[#a19d98] mb-4">Your cart is empty</p>
              <button onClick={() => setIsOpen(false)} className="btn-secondary text-sm">Continue Shopping</button>
            </div>
          ) : (
            <div className="space-y-6">
              {items.map(item => (
                <div key={item.key} className="flex gap-4" data-testid={`cart-item-${item.key}`}>
                  <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover flex-shrink-0 border border-[#b8966a]/10" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-[#f8f5f0] truncate">{item.name}</p>
                    {item.variantLabel && <p className="font-body text-xs text-[#a19d98]">{item.variantLabel}</p>}
                    {(item.deliveryDate || item.deliveryTime) && (
                      <p className="font-body text-xs text-[#b8966a]/80" data-testid={`cart-item-delivery-${item.key}`}>
                        Delivery: {item.deliveryDate}{item.deliveryTime ? ` · ${item.deliveryTime}` : ""}
                      </p>
                    )}
                    <p className="font-sans text-sm text-[#b8966a]">${item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => updateQuantity(item.key, item.quantity - 1)} className="w-6 h-6 border border-[#b8966a]/20 flex items-center justify-center text-[#a19d98] hover:text-[#b8966a] transition-colors">
                        <Minus size={12} />
                      </button>
                      <span className="font-sans text-sm text-[#f8f5f0]">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.key, item.quantity + 1)} className="w-6 h-6 border border-[#b8966a]/20 flex items-center justify-center text-[#a19d98] hover:text-[#b8966a] transition-colors">
                        <Plus size={12} />
                      </button>
                      <button onClick={() => removeItem(item.key)} className="ml-auto text-red-400/50 hover:text-red-400 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="p-6 border-t border-[#b8966a]/10 space-y-3">
            {/* Inner Circle welcome code */}
            {appliedCode ? (
              <div className="flex items-center justify-between bg-[#0c0c0c] border border-[#b8966a]/30 px-3 py-2.5" data-testid="cart-discount-applied">
                <div className="flex items-center gap-2 min-w-0">
                  <Check size={14} className="text-[#b8966a] flex-shrink-0" />
                  <span className="font-sans text-xs text-[#f8f5f0] truncate">{appliedCode} · {discountPercent}% off</span>
                </div>
                <button onClick={removeCode} className="text-[#a19d98] hover:text-red-400 transition-colors flex-shrink-0" data-testid="cart-discount-remove">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div data-testid="cart-discount-entry">
                {savedCode && (
                  <p className="font-body text-xs text-[#b8966a] mb-2" data-testid="cart-discount-reminder">
                    Don't forget your <span className="tracking-wider">{savedCode}</span> — 15% off your first order.
                  </p>
                )}
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2 bg-[#0c0c0c] border border-[#b8966a]/20 px-3">
                    <Tag size={14} className="text-[#b8966a]/60 flex-shrink-0" />
                    <input
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCode(); } }}
                      placeholder="Discount code"
                      className="w-full bg-transparent py-2.5 text-[#f8f5f0] font-body text-sm focus:outline-none uppercase placeholder:normal-case placeholder:text-[#a19d98]/50"
                      data-testid="cart-discount-input"
                    />
                  </div>
                  <button onClick={() => applyCode()} disabled={validating || !codeInput.trim()}
                    className="btn-secondary text-xs px-4 disabled:opacity-40" data-testid="cart-discount-apply">
                    {validating ? "…" : "Apply"}
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center">
              <p className="font-body text-[#a19d98]">Subtotal</p>
              <p className="font-sans text-base text-[#f8f5f0]">${total.toFixed(2)}</p>
            </div>
            {appliedCode && (
              <div className="flex justify-between items-center" data-testid="cart-discount-line">
                <p className="font-body text-[#b8966a]">Welcome discount ({discountPercent}%)</p>
                <p className="font-sans text-base text-[#b8966a]">−${discountAmount.toFixed(2)}</p>
              </div>
            )}
            <div className="flex justify-between items-center pt-1 border-t border-[#b8966a]/10">
              <p className="font-body text-[#f8f5f0]">Total</p>
              <p className="font-sans text-xl text-[#b8966a]" data-testid="cart-total">${grandTotal.toFixed(2)}</p>
            </div>
            <button onClick={handleStripeCheckout} disabled={checkingOut}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="cart-checkout-btn">
              <CreditCard size={16} />
              {checkingOut ? "Processing..." : "Pay with Card"}
            </button>
            {paypalReady && (
              <div data-testid="cart-paypal-buttons" ref={paypalContainerRef} />
            )}
            <button onClick={clearCart} className="w-full font-sans text-xs tracking-wider uppercase text-[#a19d98] hover:text-red-400 transition-colors text-center py-2">
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
}
